<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Household Details</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="View household information and members" name="description" />
    <meta content="CBHI System" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Household Details</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="/hew/households">Households</a></li>
                    <li class="breadcrumb-item active">{{ household.household_number }}</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success" role="alert">
            {{message}}
          </div>
          <div v-if="alert.info" class="alert alert-primary" role="alert">
            {{message}}
          </div>
          <div v-if="alert.failed" class="alert alert-danger" role="alert">
            {{message}}
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading household details...</p>
          </div>

          <div v-else>
            <!-- Household Header Info -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-8">
                        <div class="d-flex align-items-center mb-3">
                          <div class="avatar-lg me-3">
                            <div class="avatar-title bg-primary text-white rounded-circle">
                              <i class="bx bx-home" style="font-size: 24px;"></i>
                            </div>
                          </div>
                          <div>
                            <h4 class="mb-1">{{ household.household_number }}</h4>
                            <p class="text-muted mb-0">
                              {{ household.kebele_name }}, {{ household.woreda_name }}
                            </p>
                          </div>
                        </div>

                        <!-- Status Badges -->
                        <div class="mb-3">
                          <span v-if="household.is_model_household" class="badge bg-warning me-2">
                            <i class="bx bx-star"></i> Model Household
                          </span>
                          <span v-if="household.is_vulnerable" class="badge bg-danger me-2">
                            <i class="bx bx-shield-x"></i> Vulnerable
                          </span>
                          <span v-if="household.is_female_headed" class="badge bg-info me-2">
                            Female Headed
                          </span>
                          <span v-if="getCBHIStatus() === 'active'" class="badge bg-success me-2">
                            CBHI Active
                          </span>
                          <span v-else class="badge bg-secondary me-2">
                            Non-CBHI
                          </span>
                        </div>

                        <!-- Quick Stats -->
                        <div class="row g-3">
                          <div class="col-auto">
                            <h6 class="text-muted mb-1">Total Members</h6>
                            <h5 class="mb-0">{{ members.length }}</h5>
                          </div>
                          <div class="col-auto">
                            <h6 class="text-muted mb-1">CBHI Members</h6>
                            <h5 class="mb-0">{{ getCBHIMemberCount() }}</h5>
                          </div>
                          <div class="col-auto">
                            <h6 class="text-muted mb-1">Under 5 Children</h6>
                            <h5 class="mb-0">{{ getUnderFiveCount() }}</h5>
                          </div>
                          <div class="col-auto">
                            <h6 class="text-muted mb-1">Pregnant Women</h6>
                            <h5 class="mb-0">{{ getPregnantCount() }}</h5>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-4 text-md-end">
                        <!-- Action Buttons -->
                        <div class="d-grid gap-2 d-md-block">
                          <button @click="startVisit" class="btn btn-success">
                            <i class="bx bx-home-circle"></i> Start Visit
                          </button>
                          <button @click="showEditModal = true" class="btn btn-outline-primary">
                            <i class="bx bx-edit"></i> Edit
                          </button>
                          <div class="dropdown d-inline-block">
                            <button class="btn btn-outline-secondary dropdown-toggle" type="button"
                                    data-bs-toggle="dropdown">
                              <i class="bx bx-dots-vertical-rounded"></i> More
                            </button>
                            <div class="dropdown-menu">
                              <a class="dropdown-item" @click="showEnvironmentalModal = true">
                                <i class="bx bx-water text-primary me-2"></i> Environmental Assessment
                              </a>
                              <a class="dropdown-item" @click="showModelCriteriaModal = true">
                                <i class="bx bx-star text-warning me-2"></i> Model Household Evaluation
                              </a>
                              <div class="dropdown-divider"></div>
                              <a class="dropdown-item" @click="viewOnMap">
                                <i class="bx bx-map text-info me-2"></i> View on Map
                              </a>
                            </div>
                          </div>
                        </div>

                        <!-- GPS Coordinates -->
                        <div v-if="household.gps_latitude && household.gps_longitude" class="mt-3">
                          <small class="text-muted">
                            <i class="bx bx-map-pin"></i>
                            {{ household.gps_latitude }}, {{ household.gps_longitude }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabs -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <ul class="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
                      <li class="nav-item">
                        <a class="nav-link active" data-bs-toggle="tab" href="#members" role="tab">
                          <i class="bx bx-group"></i> Members
                        </a>
                      </li>
                      <li class="nav-item">
                        <a class="nav-link" data-bs-toggle="tab" href="#visits" role="tab">
                          <i class="bx bx-calendar-check"></i> Visit History
                        </a>
                      </li>
                      <li class="nav-item">
                        <a class="nav-link" data-bs-toggle="tab" href="#health" role="tab">
                          <i class="bx bx-heart"></i> Health Services
                        </a>
                      </li>
                      <li class="nav-item">
                        <a class="nav-link" data-bs-toggle="tab" href="#environment" role="tab">
                          <i class="bx bx-home-alt"></i> Environmental
                        </a>
                      </li>
                    </ul>

                    <div class="tab-content p-3 text-muted">
                      <!-- Members Tab -->
                      <div class="tab-pane active" id="members" role="tabpanel">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Household Members</h5>
                          <button @click="showAddMemberModal = true" class="btn btn-sm btn-primary">
                            <i class="bx bx-plus"></i> Add Member
                          </button>
                        </div>

                        <div v-if="members.length === 0" class="text-center py-4">
                          <p class="text-muted">No members registered</p>
                        </div>

                        <div v-else class="row g-3">
                          <div v-for="member in members" :key="member.member_id" class="col-md-6">
                            <div class="card border cursor-pointer" @click="viewMember(member)"
                                 :class="{'border-primary': member.member_id === household.household_head_id}">
                              <div class="card-body">
                                <div class="d-flex align-items-start">
                                  <div class="avatar-sm me-3 flex-shrink-0">
                                    <div class="avatar-title rounded-circle"
                                         :class="getGenderColor(member.gender)">
                                      <i :class="getGenderIcon(member.gender)"></i>
                                    </div>
                                  </div>
                                  <div class="flex-grow-1">
                                    <h6 class="mb-1">
                                      {{ member.first_name }} {{ member.surname }}
                                      <span v-if="member.member_id === household.household_head_id"
                                            class="badge bg-primary ms-1">Head</span>
                                    </h6>
                                    <p class="text-muted mb-2">
                                      {{ member.relationship_to_head }} •
                                      {{ getAge(member.date_of_birth) }} •
                                      {{ member.gender }}
                                    </p>

                                    <!-- Member Status -->
                                    <div class="d-flex flex-wrap gap-1">
                                      <span v-if="member.enrollment_type === 'cbhi_member'"
                                            class="badge badge-soft-success">
                                        CBHI: {{ member.cbhi_number }}
                                      </span>
                                      <span v-if="member.edd_date" class="badge badge-soft-danger">
                                        Pregnant (Due: {{ formatDate(member.edd_date) }})
                                      </span>
                                      <span v-if="isUnderFive(member.date_of_birth)"
                                            class="badge badge-soft-warning">
                                        Under 5
                                      </span>
                                      <span v-if="member.chronic_conditions"
                                            class="badge badge-soft-info">
                                        Chronic condition
                                      </span>
                                    </div>

                                    <!-- Quick Actions -->
                                    <div class="mt-2">
                                      <button v-if="isUnderFive(member.date_of_birth)"
                                              @click.stop="immunizeMember(member)"
                                              class="btn btn-sm btn-outline-success me-1">
                                        <i class="bx bx-injection"></i> Immunize
                                      </button>
                                      <button v-if="member.gender === 'Female' && getAgeInYears(member.date_of_birth) >= 15 && getAgeInYears(member.date_of_birth) <= 49"
                                              @click.stop="registerPregnancy(member)"
                                              class="btn btn-sm btn-outline-warning me-1">
                                        <i class="bx bx-female"></i> Register Pregnancy
                                      </button>
                                      <button @click.stop="createReferral(member)"
                                              class="btn btn-sm btn-outline-danger">
                                        <i class="bx bx-transfer"></i> Refer
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Visit History Tab -->
                      <div class="tab-pane" id="visits" role="tabpanel">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h5 class="mb-0">Visit History</h5>
                          <span class="text-muted">Last {{ visits.length }} visits</span>
                        </div>

                        <div v-if="visits.length === 0" class="text-center py-4">
                          <p class="text-muted">No visit history</p>
                        </div>

                        <div v-else class="timeline">
                          <div v-for="visit in visits" :key="visit.visit_id" class="timeline-item">
                            <div class="timeline-marker"></div>
                            <div class="timeline-content">
                              <div class="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h6 class="mb-1">{{ getVisitTypeLabel(visit.visit_type) }}</h6>
                                  <p class="text-muted mb-0">
                                    <i class="bx bx-calendar"></i> {{ formatDateTime(visit.visit_date) }} •
                                    <i class="bx bx-time"></i> {{ visit.visit_duration || 0 }} minutes
                                  </p>
                                </div>
                                <span :class="'badge ' + getVisitStatusBadge(visit.status)">
                                  {{ visit.status }}
                                </span>
                              </div>

                              <p v-if="visit.visit_purpose" class="mb-2">{{ visit.visit_purpose }}</p>

                              <!-- Services Provided -->
                              <div v-if="visit.services && visit.services.length > 0" class="mb-2">
                                <strong>Services Provided:</strong>
                                <ul class="mb-0">
                                  <li v-for="service in visit.services" :key="service.service_id">
                                    {{ service.service_type }}
                                    <span v-if="service.first_name">
                                      - {{ service.first_name }} {{ service.surname }}
                                    </span>
                                  </li>
                                </ul>
                              </div>

                              <div class="d-flex justify-content-between align-items-center">
                                <small class="text-muted">
                                  By: {{ visit.hew_name }}
                                </small>
                                <button @click="viewVisitDetails(visit)" class="btn btn-sm btn-outline-primary">
                                  View Details
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Health Services Tab -->
                      <div class="tab-pane" id="health" role="tabpanel">
                        <div class="row">
                          <!-- Immunizations -->
                          <div class="col-md-6">
                            <h5 class="mb-3">Recent Immunizations</h5>
                            <div v-if="recentImmunizations.length === 0" class="text-center py-4">
                              <p class="text-muted">No recent immunizations</p>
                            </div>
                            <div v-else class="list-group">
                              <div v-for="imm in recentImmunizations" :key="imm.immunization_id"
                                   class="list-group-item">
                                <div class="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 class="mb-0">{{ imm.vaccine_name }} - Dose {{ imm.dose_number }}</h6>
                                    <small class="text-muted">
                                      {{ imm.member_name }} • {{ formatDate(imm.date_given) }}
                                    </small>
                                  </div>
                                  <i class="bx bx-check-circle text-success"></i>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Health Education -->
                          <div class="col-md-6">
                            <h5 class="mb-3">Health Education Sessions</h5>
                            <div v-if="healthEducationSessions.length === 0" class="text-center py-4">
                              <p class="text-muted">No education sessions recorded</p>
                            </div>
                            <div v-else class="list-group">
                              <div v-for="session in healthEducationSessions" :key="session.session_id"
                                   class="list-group-item">
                                <h6 class="mb-0">{{ session.topic }}</h6>
                                <small class="text-muted">
                                  {{ formatDate(session.session_date) }} •
                                  {{ session.attendees_count }} attendees
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Add Services Button -->
                        <div class="text-center mt-4">
                          <button @click="startVisit" class="btn btn-primary">
                            <i class="bx bx-plus"></i> Record New Services
                          </button>
                        </div>
                      </div>

                      <!-- Environmental Tab -->
                      <div class="tab-pane" id="environment" role="tabpanel">
                        <div v-if="!environmentalAssessment" class="text-center py-4">
                          <i class="bx bx-home-alt text-muted" style="font-size: 48px;"></i>
                          <p class="mt-3 text-muted">No environmental assessment conducted</p>
                          <button @click="showEnvironmentalModal = true" class="btn btn-primary">
                            Conduct Assessment
                          </button>
                        </div>

                        <div v-else>
                          <div class="d-flex justify-content-between align-items-center mb-3">
                            <h5 class="mb-0">Latest Environmental Assessment</h5>
                            <span class="text-muted">
                              {{ formatDate(environmentalAssessment.assessment_date) }}
                            </span>
                          </div>

                          <div class="row g-3">
                            <!-- Water & Sanitation -->
                            <div class="col-md-6">
                              <div class="card border">
                                <div class="card-header bg-light">
                                  <h6 class="mb-0">Water & Sanitation</h6>
                                </div>
                                <div class="card-body">
                                  <div class="mb-2">
                                    <strong>Water Source:</strong> {{ environmentalAssessment.water_source_type }}
                                    <span v-if="environmentalAssessment.water_source_protected"
                                          class="badge bg-success ms-1">Protected</span>
                                  </div>
                                  <div class="mb-2">
                                    <strong>Treatment Method:</strong>
                                    {{ environmentalAssessment.water_treatment_method || 'None' }}
                                  </div>
                                  <div class="mb-2">
                                    <strong>Storage:</strong>
                                    <span v-if="environmentalAssessment.water_storage_safe"
                                          class="text-success">Safe</span>
                                    <span v-else class="text-danger">Unsafe</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Latrine -->
                            <div class="col-md-6">
                              <div class="card border">
                                <div class="card-header bg-light">
                                  <h6 class="mb-0">Latrine Facility</h6>
                                </div>
                                <div class="card-body">
                                  <div v-if="environmentalAssessment.latrine_available">
                                    <div class="mb-2">
                                      <strong>Type:</strong> {{ environmentalAssessment.latrine_type }}
                                    </div>
                                    <div class="mb-2">
                                      <strong>Condition:</strong> {{ environmentalAssessment.latrine_condition }}
                                    </div>
                                    <div>
                                      <strong>Shared:</strong>
                                      {{ environmentalAssessment.latrine_shared ? 'Yes' : 'No' }}
                                    </div>
                                  </div>
                                  <div v-else class="text-danger">
                                    <i class="bx bx-x-circle"></i> No latrine available
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Hygiene -->
                            <div class="col-md-6">
                              <div class="card border">
                                <div class="card-header bg-light">
                                  <h6 class="mb-0">Hygiene Practices</h6>
                                </div>
                                <div class="card-body">
                                  <div class="d-flex justify-content-between mb-2">
                                    <span>Handwashing Facility</span>
                                    <span v-if="environmentalAssessment.handwashing_facility"
                                          class="text-success">
                                      <i class="bx bx-check"></i> Yes
                                    </span>
                                    <span v-else class="text-danger">
                                      <i class="bx bx-x"></i> No
                                    </span>
                                  </div>
                                  <div class="d-flex justify-content-between">
                                    <span>Soap Available</span>
                                    <span v-if="environmentalAssessment.soap_available"
                                          class="text-success">
                                      <i class="bx bx-check"></i> Yes
                                    </span>
                                    <span v-else class="text-danger">
                                      <i class="bx bx-x"></i> No
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Kitchen & Food -->
                            <div class="col-md-6">
                              <div class="card border">
                                <div class="card-header bg-light">
                                  <h6 class="mb-0">Kitchen & Food Safety</h6>
                                </div>
                                <div class="card-body">
                                  <div class="d-flex justify-content-between mb-2">
                                    <span>Separate Kitchen</span>
                                    <span v-if="environmentalAssessment.kitchen_separate"
                                          class="text-success">
                                      <i class="bx bx-check"></i> Yes
                                    </span>
                                    <span v-else class="text-danger">
                                      <i class="bx bx-x"></i> No
                                    </span>
                                  </div>
                                  <div class="mb-2">
                                    <strong>Ventilation:</strong>
                                    {{ environmentalAssessment.kitchen_ventilation }}
                                  </div>
                                  <div class="d-flex justify-content-between">
                                    <span>Safe Food Storage</span>
                                    <span v-if="environmentalAssessment.food_storage_safe"
                                          class="text-success">
                                      <i class="bx bx-check"></i> Yes
                                    </span>
                                    <span v-else class="text-danger">
                                      <i class="bx bx-x"></i> No
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Recommendations -->
                          <div v-if="environmentalAssessment.recommendations" class="mt-3">
                            <h6>Recommendations</h6>
                            <div class="alert alert-info">
                              {{ environmentalAssessment.recommendations }}
                            </div>
                          </div>

                          <!-- Update Assessment Button -->
                          <div class="text-center mt-3">
                            <button @click="showEnvironmentalModal = true" class="btn btn-primary">
                              Update Assessment
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <!-- Import Modal Components -->
      <EditHouseholdModal
          v-if="showEditModal"
          :household="household"
          @close="showEditModal = false"
          @updated="onHouseholdUpdated"
      />


      <AddMemberModal
          v-if="showAddMemberModal"
          :household="household"
          :householdId="householdId"
          @close="showAddMemberModal = false"
          @added="onMemberAdded"
      />

      <StartVisitModal
          v-if="showVisitModal"
          :household="household"
          @close="showVisitModal = false"
          @started="onVisitStarted"
      />

      <ImmunizationModal
          v-if="showImmunizationModal"
          :preselectedMember="selectedMember"
          @close="showImmunizationModal = false"
          @recorded="onImmunizationRecorded"
      />

      <PregnancyRegistrationModal
          v-if="showPregnancyModal"
          :preselectedMember="selectedMember"
          @close="showPregnancyModal = false"
          @registered="onPregnancyRegistered"
      />

      <ReferralModal
          v-if="showReferralModal"
          :preselectedMember="selectedMember"
          @close="showReferralModal = false"
          @created="onReferralCreated"
      />

      <EnvironmentalAssessmentModal
          v-if="showEnvironmentalModal"
          :householdId="householdId"
          :existingAssessment="environmentalAssessment"
          @close="showEnvironmentalModal = false"
          @saved="onEnvironmentalAssessmentSaved"
      />

      <ModelHouseholdEvaluationModal
          v-if="showModelCriteriaModal"
          :householdId="householdId"
          @close="showModelCriteriaModal = false"
          @evaluated="onModelEvaluated"
      />

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import EditHouseholdModal from "@/components/hew/modals/EditHouseholdModal.vue";
import AddMemberModal from "@/components/hew/modals/AddMemberModal.vue";
import StartVisitModal from "@/components/hew/modals/StartVisitModal.vue";
import ImmunizationModal from "@/components/hew/modals/ImmunizationModal.vue";
import PregnancyRegistrationModal from "@/components/hew/modals/PregnancyRegistrationModal.vue";
import ReferralModal from "@/components/hew/modals/ReferralModal.vue";
import EnvironmentalAssessmentModal from "@/components/hew/modals/EnvironmentalAssessmentModal.vue";
import ModelHouseholdEvaluationModal from "@/components/hew/modals/ModelHouseholdEvaluationModal.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "HouseholdDetails",
  components: {
    NavigationBar,
    EditHouseholdModal,
    AddMemberModal,
    StartVisitModal,
    ImmunizationModal,
    PregnancyRegistrationModal,
    ReferralModal,
    EnvironmentalAssessmentModal,
    ModelHouseholdEvaluationModal
  },
  data() {
    return {
      // Loading states
      loading: false,

      // Household data
      householdId: null,
      household: {},
      members: [],
      visits: [],
      environmentalAssessment: null,
      recentImmunizations: [],
      healthEducationSessions: [],

      // Selected member for actions
      selectedMember: null,

      // Modal states
      showEditModal: false,
      showAddMemberModal: false,
      showVisitModal: false,
      showImmunizationModal: false,
      showPregnancyModal: false,
      showReferralModal: false,
      showEnvironmentalModal: false,
      showModelCriteriaModal: false,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  mounted() {

    this.householdId = sessionStorage.getItem('household_id')
    this.loadHouseholdDetails();
  },

  methods: {


    async loadHouseholdDetails() {
      this.loading = true;

      const data = {
        "function": "getHouseholdDetails",
        "householdId": this.householdId
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.household = res.data.data.household || {};
          this.members = res.data.data.members || [];
          this.visits = res.data.data.visits || [];
          this.environmentalAssessment = res.data.data.environmentalAssessment;

          // Load additional health data
          this.loadHealthData();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load household details');
        }
      } catch (e) {
        console.error("Failed to load household details:", e);
        this.showAlert('failed', 'Failed to load household details');
      } finally {
        this.loading = false;
      }
    },

    async loadHealthData() {
      // This would load recent immunizations and health education sessions
      // For now, using placeholder data
      this.recentImmunizations = this.members
          .filter(m => this.isUnderFive(m.date_of_birth) && m.vaccination_count > 0)
          .map(m => ({
            member_name: `${m.first_name} ${m.surname}`,
            vaccine_name: 'BCG',
            dose_number: 1,
            date_given: '2024-01-15'
          }))
          .slice(0, 5);

      this.healthEducationSessions = [
        {
          session_id: 1,
          topic: 'Hygiene and Sanitation',
          session_date: '2024-01-10',
          attendees_count: 3
        }
      ];
    },

    // Actions
    startVisit() {
      this.showVisitModal = true;
    },

    viewMember(member) {
      sessionStorage.setItem("memberId",member.member_id)
      router.push(`member-profile`);
    },

    immunizeMember(member) {
      this.selectedMember = member;
      this.showImmunizationModal = true;
    },

    registerPregnancy(member) {
      this.selectedMember = member;
      this.showPregnancyModal = true;
    },

    createReferral(member) {
      this.selectedMember = member;
      this.showReferralModal = true;
    },

    viewVisitDetails(visit) {
      sessionStorage.setItem("visitId", visit.visit_id)
      router.push(`visit-details`);
    },

    viewOnMap() {
      if (this.household.gps_latitude && this.household.gps_longitude) {
        // Open map view with coordinates
        window.open(`https://maps.google.com/?q=${this.household.gps_latitude},${this.household.gps_longitude}`, '_blank');
      }
    },

    // Modal callbacks
    onHouseholdUpdated() {
      this.showEditModal = false;
      this.showAlert('success', 'Household updated successfully');
      this.loadHouseholdDetails();
    },

    onMemberAdded() {
      this.showAddMemberModal = false;
      this.showAlert('success', 'Member added successfully');
      this.loadHouseholdDetails();
    },

    onVisitStarted(visitData) {
      this.showVisitModal = false;
      sessionStorage.setItem("visitId",visitData.visitId)
      router.push(`visit-details`);
    },

    onImmunizationRecorded() {
      this.showImmunizationModal = false;
      this.selectedMember = null;
      this.showAlert('success', 'Immunization recorded successfully');
      this.loadHouseholdDetails();
    },

    onPregnancyRegistered() {
      this.showPregnancyModal = false;
      this.selectedMember = null;
      this.showAlert('success', 'Pregnancy registered successfully');
      this.loadHouseholdDetails();
    },

    onReferralCreated() {
      this.showReferralModal = false;
      this.selectedMember = null;
      this.showAlert('success', 'Referral created successfully');
    },

    onEnvironmentalAssessmentSaved() {
      this.showEnvironmentalModal = false;
      this.showAlert('success', 'Environmental assessment saved successfully');
      this.loadHouseholdDetails();
    },

    onModelEvaluated() {
      this.showModelCriteriaModal = false;
      this.showAlert('success', 'Model household evaluation completed');
      this.loadHouseholdDetails();
    },

    // Utility methods
    getCBHIStatus() {
      return this.members.some(m => m.enrollment_type === 'cbhi_member') ? 'active' : 'inactive';
    },

    getCBHIMemberCount() {
      return this.members.filter(m => m.enrollment_type === 'cbhi_member').length;
    },

    getUnderFiveCount() {
      return this.members.filter(m => this.isUnderFive(m.date_of_birth)).length;
    },

    getPregnantCount() {
      return this.members.filter(m => m.edd_date).length;
    },

    isUnderFive(dateOfBirth) {
      if (!dateOfBirth) return false;
      const age = this.getAgeInYears(dateOfBirth);
      return age < 5;
    },

    getAge(dateOfBirth) {
      if (!dateOfBirth) return 'Unknown';

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const ageInYears = today.getFullYear() - birthDate.getFullYear();

      if (ageInYears < 1) {
        const ageInMonths = (today.getFullYear() - birthDate.getFullYear()) * 12 +
            (today.getMonth() - birthDate.getMonth());
        return `${ageInMonths} months`;
      }

      return `${ageInYears} years`;
    },

    getAgeInYears(dateOfBirth) {
      if (!dateOfBirth) return 0;
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      return today.getFullYear() - birthDate.getFullYear();
    },

    getGenderIcon(gender) {
      return gender === 'Male' ? 'bx bx-male' : 'bx bx-female';
    },

    getGenderColor(gender) {
      return gender === 'Male' ? 'bg-primary' : 'bg-danger';
    },

    getVisitTypeLabel(type) {
      const labels = {
        'routine': 'Routine Health Promotion',
        'follow_up': 'Follow-up Visit',
        'emergency': 'Emergency Visit',
        'campaign': 'Campaign Activity',
        'assessment': 'Environmental Assessment'
      };
      return labels[type] || type;
    },

    getVisitStatusBadge(status) {
      const badges = {
        'ongoing': 'bg-warning',
        'completed': 'bg-success',
        'cancelled': 'bg-danger'
      };
      return badges[status] || 'bg-secondary';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // Alert methods
    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  transition: all 0.2s;
}

.nav-tabs .nav-link {
  color: #495057;
  border: none;
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link.active {
  color: #556ee6;
  background-color: transparent;
  border-color: #556ee6;
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 9px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}

.timeline-item {
  position: relative;
  padding-bottom: 30px;
}

.timeline-marker {
  position: absolute;
  left: -21px;
  top: 5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #556ee6;
  border: 2px solid #fff;
  box-shadow: 0 0 0 3px #e9ecef;
}

.timeline-content {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.badge-soft-success {
  color: #34c38f;
  background-color: rgba(52, 195, 143, 0.18);
}

.badge-soft-danger {
  color: #f46a6a;
  background-color: rgba(244, 106, 106, 0.18);
}

.badge-soft-warning {
  color: #f1b44c;
  background-color: rgba(241, 180, 76, 0.18);
}

.badge-soft-info {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.18);
}
</style>